/**
 * useErasure — single source of truth for the family-deletion grace state.
 *
 * Models exactly four states (mirrors the handoff's rc_erasure.jsx):
 *   no_request        — nothing pending (default)
 *   grace_pending     — schedule fired; 30-day window running
 *   grace_cancelled   — cancel returned 200 in time (transient — relaxes
 *                       back to no_request shortly after, like the prototype,
 *                       so the GraceBanner can show its "welcome back" pulse)
 *   grace_completed   — window elapsed; the hard delete ran
 *
 * Source of truth for ``scheduledAt`` is the server: it comes back from
 * /family/erasure or /family/erasure/email-confirm as
 * ``scheduled_deletion_at``. We also derive it locally from useBilling's
 * ``erasure_requested_at + 30d`` so the state survives a refresh — but
 * the freshest source wins.
 */

import { useEffect, useState } from "react";
import { useBilling } from "../billing/useBilling";
import {
  GraceAlreadyElapsedError,
  PasswordMismatchError,
  cancelErasure as apiCancel,
  emailConfirm as apiEmailConfirm,
  requestErasure as apiRequest,
} from "./api";

export const GRACE_DAYS = 30;
const DAY_MS = 86_400_000;

export type ErasureStatus =
  | "no_request"
  | "grace_pending"
  | "grace_cancelled"
  | "grace_completed";

interface ErasureState {
  status: ErasureStatus;
  /** ISO timestamp the hard-delete is scheduled for, or null. */
  scheduledAt: string | null;
  /** When the request was made, or null. */
  requestedAt: string | null;
}

interface ErasureStore {
  state: ErasureState;
  listeners: Set<() => void>;
}

const store: ErasureStore = {
  state: { status: "no_request", scheduledAt: null, requestedAt: null },
  listeners: new Set(),
};

function setStore(patch: Partial<ErasureState>): void {
  store.state = { ...store.state, ...patch };
  for (const fn of store.listeners) fn();
}

export interface UseErasureValue {
  status: ErasureStatus;
  scheduledAt: string | null;
  requestedAt: string | null;
  /** True iff the 30-day window is running. */
  pending: boolean;
  /** True iff the hard delete has already run. */
  completed: boolean;
  /** Days remaining (rounded up). 0 when not pending. */
  daysLeft: number;
  /** Localized date string for the deletion, or null. */
  scheduledStr: string | null;
  /** Localized date string for the original request, or null. */
  requestedStr: string | null;

  /** Schedule the deletion. Throws PasswordMismatchError on bad password. */
  request: (password: string) => Promise<void>;
  /** Cancel the deletion. Throws GraceAlreadyElapsedError on 410. */
  cancel: () => Promise<void>;
  /** Confirm via the emailed token. Throws InvalidTokenError on bad token. */
  confirmEmailToken: (token: string) => Promise<void>;
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function daysLeft(scheduledAt: string | null): number {
  if (!scheduledAt) return 0;
  const ms = new Date(scheduledAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / DAY_MS));
}

export function useErasure(): UseErasureValue {
  const b = useBilling();
  const [, force] = useState(0);

  useEffect(() => {
    const fn = () => force((n) => n + 1);
    store.listeners.add(fn);
    return () => {
      store.listeners.delete(fn);
    };
  }, []);

  // Reconcile from the server-cached billing state on every render. The
  // billing hook is the durable source for ``erasure_requested_at``; the
  // local store is for the freshly-returned ``scheduled_deletion_at`` and
  // the transient ``grace_cancelled`` pulse.
  useEffect(() => {
    const serverRequestedAt = b.erasure_requested_at ?? null;
    if (serverRequestedAt && store.state.status === "no_request") {
      // Came in fresh from a page reload — promote to grace_pending.
      const scheduled = new Date(
        new Date(serverRequestedAt).getTime() + GRACE_DAYS * DAY_MS,
      ).toISOString();
      const elapsed = new Date(scheduled).getTime() <= Date.now();
      setStore({
        status: elapsed ? "grace_completed" : "grace_pending",
        scheduledAt: scheduled,
        requestedAt: serverRequestedAt,
      });
    } else if (
      !serverRequestedAt
      && store.state.status === "grace_pending"
    ) {
      // Server cleared it (e.g. cancel succeeded elsewhere).
      setStore({
        status: "no_request",
        scheduledAt: null,
        requestedAt: null,
      });
    }
  }, [b.erasure_requested_at]);

  const { status, scheduledAt, requestedAt } = store.state;

  return {
    status,
    scheduledAt,
    requestedAt,
    pending: status === "grace_pending",
    completed: status === "grace_completed",
    daysLeft: daysLeft(scheduledAt),
    scheduledStr: fmtDate(scheduledAt),
    requestedStr: fmtDate(requestedAt),

    request: async (password) => {
      const resp = await apiRequest(password);
      setStore({
        status: "grace_pending",
        scheduledAt: resp.scheduled_deletion_at,
        requestedAt: new Date().toISOString(),
      });
      await b.refresh();
    },

    cancel: async () => {
      try {
        await apiCancel();
        setStore({
          status: "grace_cancelled",
          scheduledAt: null,
        });
        // Settle back to no_request shortly after so the brief "welcome
        // back" pulse can clear — mirrors rc_erasure.jsx's behaviour.
        setTimeout(() => {
          if (store.state.status === "grace_cancelled") {
            setStore({
              status: "no_request",
              scheduledAt: null,
              requestedAt: null,
            });
          }
        }, 1800);
        await b.refresh();
      } catch (err) {
        if (err instanceof GraceAlreadyElapsedError) {
          setStore({
            status: "grace_completed",
            scheduledAt,
          });
        }
        throw err;
      }
    },

    confirmEmailToken: async (token) => {
      const resp = await apiEmailConfirm(token);
      setStore({
        status: "grace_pending",
        scheduledAt: resp.scheduled_deletion_at,
        requestedAt: new Date().toISOString(),
      });
    },
  };
}

export { PasswordMismatchError, GraceAlreadyElapsedError };
export { InvalidTokenError } from "./api";

/** Test-only: reset the in-memory store between cases. */
export function __resetErasureStore(): void {
  store.state = { status: "no_request", scheduledAt: null, requestedAt: null };
  for (const fn of store.listeners) fn();
}

/** Test-only: force a specific state for matrix rendering. */
export function __setErasureStore(state: Partial<ErasureState>): void {
  setStore(state);
}
